<script setup lang="ts">
/**
 * `Group` column of the services table: group name over the group's age
 * ("1 y 4 mo"). Renders a muted dash for ad-hoc services that belong to no
 * group, so the column never looks broken.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { groupAgeShort } from '@/utils/groupAge'

const props = defineProps<{
  /** Display name; omit for a service without a group. */
  name?: string
  /** Whole years since the group started. */
  years?: number
  /** Whole months (0-11) since the group started, after `years`. */
  months?: number
  /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
  maxWidth?: string
}>()

const { t } = useI18n()

const age = computed(() => groupAgeShort(props.years ?? 0, props.months ?? 0, t))
</script>

<template>
  <td
    class="service-group-cell font-tufte text-tufte-text"
    :style="maxWidth ? { maxWidth: maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <template v-if="name">
      <div class="service-group-cell__name">{{ name }}</div>
      <div class="service-group-cell__age">{{ age }}</div>
    </template>
    <span v-else class="text-tufte-muted">–</span>
  </td>
</template>

<style scoped>
.service-group-cell__name {
  font-size: var(--text-tufte-lg);
  font-weight: 500;
}

.service-group-cell__age {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  margin-top: 0.12rem;
}
</style>
